/**
 * SnapshotPanels.jsx
 *
 * Live Sales Snapshot + Live Marketing Snapshot panels shared between the
 * CEO Dashboard and the HOD Dashboard. Both panels accept an optional
 * `token` and `onTargetsSaved` callback — when both are present, an
 * "Edit Targets" button is shown that opens a dialog allowing the CEO or
 * HOD to update the underlying targets (weekly sales goal, lead-gen
 * target, monthly ad spend). The save call hits PUT /api/dashboard/targets
 * and the parent is expected to re-fetch its snapshot afterwards.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import HelpInfo from './HelpInfo';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogBody } from './ui/dialog';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  BarChart3,
  Megaphone,
  DollarSign,
  TrendingUp,
  Users,
  Target,
  Mail,
  Pencil,
  Save,
  RefreshCw,
} from 'lucide-react';
import DirhamSign from './DirhamSign';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Watches the html element for the `dark` class so the panels follow the
// app-wide theme without needing a context import.
const useDarkMode = () => {
  const [darkMode, setDarkMode] = useState(false);
  useEffect(() => {
    const check = () => setDarkMode(document.documentElement.classList.contains('dark'));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return darkMode;
};

// ============ Edit Targets Dialog ============
// Lets CEO/HODs edit the underlying targets that drive the snapshot panels.
// Opens with the current values prefilled so the user only has to change
// what they want to change. On Save, PATCH-style send only the fields whose
// values actually differ from the loaded ones (the server still accepts
// any subset, but skipping unchanged fields keeps audit trails honest).
export const EditTargetsDialog = ({ open, onOpenChange, token, scope = 'all', onSaved }) => {
  const darkMode = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState({});
  const [form, setForm] = useState({
    weekly_sales_target: '',
    leads_target: '',
    ad_spend: '',
  });
  // Ref to the first visible input so we can move focus there on open
  // instead of the default (which would land on the X close button and
  // show a fat focus ring before the user has done anything).
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!open || !token) return;
    setLoading(true);
    fetch(`${API_URL}/api/dashboard/targets`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((j) => {
        if (j?.success) {
          const t = j.data || {};
          const prefill = {
            weekly_sales_target: t.weekly_sales_target ?? '',
            leads_target: t.leads_target ?? '',
            ad_spend: t.ad_spend ?? '',
          };
          setForm(prefill);
          setOriginal(prefill);
        }
      })
      .catch(() => toast.error('Could not load targets'))
      .finally(() => setLoading(false));
  }, [open, token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send fields the user actually changed. The backend treats an
      // omitted field as "leave alone", so this preserves any values that
      // another role had previously set without us having to know them.
      const payload = {};
      for (const k of Object.keys(form)) {
        if (form[k] === '' || form[k] === null) continue;
        const num = Number(form[k]);
        if (Number.isNaN(num)) continue;
        if (num !== Number(original[k])) payload[k] = num;
      }
      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }
      const res = await fetch(`${API_URL}/api/dashboard/targets`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.detail || 'Failed to save');
      }
      toast.success('Targets updated');
      onOpenChange?.(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.message || 'Failed to save targets');
    } finally {
      setSaving(false);
    }
  };

  // Sales HODs typically only care about the weekly sales target; marketing
  // HODs typically only care about the lead-gen target and ad spend. The
  // scope prop hides irrelevant fields so the dialog stays focused. CEO and
  // the default "all" scope show everything.
  const showSales = scope === 'all' || scope === 'sales';
  const showMarketing = scope === 'all' || scope === 'marketing';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-md p-0 ${darkMode ? 'bg-[#1e1e2f] border-slate-700' : ''}`}
        // The dialog component renders its own X button which Radix
        // auto-focuses on open — that's what was producing the chunky
        // focus ring around the close icon. Suppress the default and
        // hand focus to the first input instead so the user can start
        // typing immediately.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          // Defer one tick so the input has mounted before we try.
          setTimeout(() => firstInputRef.current?.focus(), 0);
        }}
      >
        <DialogHeader>
          <DialogTitle className={darkMode ? 'text-white' : ''}>Edit Targets</DialogTitle>
          <DialogDescription className={darkMode ? 'text-slate-400' : ''}>
            Set the goals the snapshot panels measure progress against.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <DialogBody>
            <div className="flex items-center justify-center py-4">
              <RefreshCw size={20} className="animate-spin text-violet-500" />
            </div>
          </DialogBody>
        ) : (
          <DialogBody className="space-y-4">
            {showSales && (
              <div className="space-y-1.5">
                <label
                  htmlFor="weekly_sales_target"
                  className={`text-sm font-medium block ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}
                >
                  Weekly Sales Target (AED)
                </label>
                <Input
                  id="weekly_sales_target"
                  ref={firstInputRef}
                  type="number"
                  min="0"
                  step="100000"
                  value={form.weekly_sales_target}
                  onChange={(e) => setForm({ ...form, weekly_sales_target: e.target.value })}
                  placeholder="5000000"
                />
                <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Drives the Weekly Target Progress bar on Sales Snapshot.
                </p>
              </div>
            )}

            {showMarketing && (
              <>
                <div className="space-y-1.5">
                  <label
                    htmlFor="leads_target"
                    className={`text-sm font-medium block ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}
                  >
                    Lead Generation Target
                  </label>
                  <Input
                    id="leads_target"
                    // When sales is hidden, the leads field becomes the
                    // first focusable input — capture the ref here too.
                    ref={showSales ? undefined : firstInputRef}
                    type="number"
                    min="0"
                    step="10"
                    value={form.leads_target}
                    onChange={(e) => setForm({ ...form, leads_target: e.target.value })}
                    placeholder="100"
                  />
                  <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Drives the Lead Generation Target bar on Marketing Snapshot.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="ad_spend"
                    className={`text-sm font-medium block ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}
                  >
                    Monthly Ad Spend (AED)
                  </label>
                  <Input
                    id="ad_spend"
                    type="number"
                    min="0"
                    step="500"
                    value={form.ad_spend}
                    onChange={(e) => setForm({ ...form, ad_spend: e.target.value })}
                    placeholder="0"
                  />
                  <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Reported on the Ad Spend tile (no automated source — manual entry).
                  </p>
                </div>
              </>
            )}
          </DialogBody>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={saving || loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            {saving ? (
              <><RefreshCw size={14} className="mr-1.5 animate-spin" /> Saving</>
            ) : (
              <><Save size={14} className="mr-1.5" /> Save Targets</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ Sales Snapshot Panel ============
export const SalesSnapshotPanel = ({ data, token, onTargetsSaved, editScope = 'sales' }) => {
  const darkMode = useDarkMode();
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = Boolean(token && onTargetsSaved);

  if (!data) return null;

  const metrics = [
    { label: 'Deals Today', value: data.deals_today?.count || 0, subtext: <><DirhamSign /> {(data.deals_today?.value || 0).toLocaleString()}</>, icon: DirhamSign, color: 'text-emerald-500' },
    { label: 'Pipeline Value', value: <><DirhamSign /> {(data.pipeline_value || 0).toLocaleString()}</>, subtext: 'Active deals', icon: TrendingUp, color: 'text-blue-500' },
    { label: 'New Leads Today', value: data.leads_today || 0, subtext: `${data.conversion_rate_week || 0}% conversion`, icon: Users, color: 'text-purple-500' },
    { label: 'Agents Active', value: `${data.agents?.active_today || 0} / ${data.agents?.total || 0}`, subtext: 'Today', icon: Target, color: 'text-amber-500' },
  ];

  return (
    <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className={`text-base flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : ''}`}>
              <BarChart3 size={18} className="text-blue-500" />
              Live Sales Snapshot
              <HelpInfo
                align="inline"
                title="How to read Live Sales Snapshot"
                steps={[
                  'Four tiles that give you the sales pulse in one glance: deals closed today, total pipeline value, weekly target progress, and monthly ad spend.',
                  <>Numbers refresh on their own every 5 minutes — no need to reload. The progress bar on <em>Weekly Target</em> turns from amber to green as you cross the goal.</>,
                  <>The targets (weekly sales goal, lead-gen goal, monthly ad spend) come from operator settings. CEO and Admins see an <em>Edit Targets</em> button to update them inline.</>,
                  'Drilling down lives in the tabs below: Pipeline-by-stage, agent performance, and per-deal detail.',
                ]}
                tip="If pipeline value is high but weekly target progress is low, conversion is the bottleneck — not lead generation."
              />
            </CardTitle>
            <CardDescription className={darkMode ? 'text-slate-400' : ''}>
              Sales department metrics • Auto-refreshes every 5 minutes
            </CardDescription>
          </div>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => setEditOpen(true)}
              title="Edit targets"
            >
              <Pencil size={14} className="mr-1" />
              Edit Targets
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((metric, idx) => (
            <div key={idx} className={`p-4 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <metric.icon size={16} className={metric.color} />
                <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {metric.label}
                </span>
              </div>
              <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {metric.value}
              </p>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {metric.subtext}
              </p>
            </div>
          ))}
        </div>

        <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-700'}`}>
              Weekly Target Progress
            </span>
            <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <DirhamSign /> {(data.deals_week?.value || 0).toLocaleString()} / {(data.deals_week?.target || 5000000).toLocaleString()}
            </span>
          </div>
          <Progress value={data.deals_week?.progress_percent || 0} className="h-3" />
          <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            {data.deals_week?.count || 0} deals closed this week ({data.deals_week?.progress_percent || 0}% of target)
          </p>
        </div>
      </CardContent>

      {canEdit && (
        <EditTargetsDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          token={token}
          scope={editScope}
          onSaved={onTargetsSaved}
        />
      )}
    </Card>
  );
};

// ============ Marketing Snapshot Panel ============
export const MarketingSnapshotPanel = ({ data, token, onTargetsSaved, editScope = 'marketing' }) => {
  const darkMode = useDarkMode();
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = Boolean(token && onTargetsSaved);

  if (!data) return null;

  const metrics = [
    { label: 'Campaigns Active', value: data.campaigns_active || 0, subtext: `${data.campaigns_paused || 0} paused`, icon: Megaphone, color: 'text-purple-500' },
    { label: 'Leads Generated', value: data.leads_generated || 0, subtext: 'This week', icon: Users, color: 'text-cyan-500' },
    { label: 'Email Open Rate', value: `${data.email_open_rate || 0}%`, subtext: `${data.emails_sent || 0} sent`, icon: Mail, color: 'text-emerald-500' },
    { label: 'Ad Spend', value: <><DirhamSign /> {(data.ad_spend || 0).toLocaleString()}</>, subtext: 'This month', icon: DirhamSign, color: 'text-amber-500' },
  ];

  return (
    <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className={`text-base flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : ''}`}>
              <Megaphone size={18} className="text-purple-500" />
              Live Marketing Snapshot
              <HelpInfo
                align="inline"
                title="How to read Live Marketing Snapshot"
                steps={[
                  'Four tiles for the marketing pulse: leads generated this month, conversion rate, cost per lead, and ad spend.',
                  'Conversion rate = leads that became deals divided by total leads — a good signal for lead quality, not just volume.',
                  <>Cost per lead is calculated against the same period\'s ad spend so the two tiles tell a coherent story. If CPL is climbing while ad spend stays flat, the channel is getting more expensive.</>,
                  <>Numbers auto-refresh every 5 minutes. CEO/Admins can adjust the monthly lead-gen target and ad spend via <em>Edit Targets</em>.</>,
                ]}
                tip="Match this against the Sales snapshot — high lead volume + low conversion means the handoff to sales needs attention, not more ads."
              />
            </CardTitle>
            <CardDescription className={darkMode ? 'text-slate-400' : ''}>
              Marketing department metrics • Auto-refreshes every 5 minutes
            </CardDescription>
          </div>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => setEditOpen(true)}
              title="Edit targets"
            >
              <Pencil size={14} className="mr-1" />
              Edit Targets
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((metric, idx) => (
            <div key={idx} className={`p-4 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <metric.icon size={16} className={metric.color} />
                <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {metric.label}
                </span>
              </div>
              <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {metric.value}
              </p>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {metric.subtext}
              </p>
            </div>
          ))}
        </div>

        <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-700'}`}>
              Lead Generation Target
            </span>
            <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {data.leads_generated || 0} / {data.leads_target || 100} leads
            </span>
          </div>
          <Progress value={data.leads_target ? ((data.leads_generated || 0) / data.leads_target * 100) : 0} className="h-3" />
          <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            {data.conversion_rate || 0}% lead-to-customer conversion this month
          </p>
        </div>
      </CardContent>

      {canEdit && (
        <EditTargetsDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          token={token}
          scope={editScope}
          onSaved={onTargetsSaved}
        />
      )}
    </Card>
  );
};
